// Resolved through the @brands alias rather than './types/ICase' so this
// checks against the running brand's CaseType. Each brand may carry its own
// copy of the enum, and a relative import would silently validate against
// default's after one of them gains a case type.
import { CaseType } from '@brands/services/cases/types/ICase';

/**
 * Case types that still exist in the API enum — and in historical pricing
 * rows, so the backend's pricing lookup accepts them — but that no surface
 * offers any more. Instant video call lost its live provider queue in 2023 and
 * in-app messaging was never launched; a case created with either gets no
 * clinician and no appointment, and then renders blank everywhere because the
 * grids and the consultation page have no branch for it.
 */
export const RETIRED_CASE_TYPES: readonly CaseType[] = [CaseType.video_call_instant, CaseType.messaging];

/** The case types a patient can still start an intake for. */
export const STARTABLE_CASE_TYPES: readonly CaseType[] = Object.values(CaseType).filter(
  // CaseType.unknown is the enum's empty-string placeholder, not a service.
  (type) => type !== CaseType.unknown && !RETIRED_CASE_TYPES.includes(type),
);

const STARTABLE = new Set<string>(STARTABLE_CASE_TYPES);

/**
 * Whether a raw string (a URL parameter, a stored intent) names a case type a
 * patient is allowed to start an intake for. Membership in the `CaseType` enum
 * is not enough on its own — that is what let `?start_case=video_call_instant`
 * through.
 */
export const isStartableCaseType = (value: unknown): value is CaseType =>
  typeof value === 'string' && STARTABLE.has(value);
